import { useEffect, useState, useCallback, useMemo, useRef } from 'react'
import { observer, inject } from 'mobx-react'
import { toJS } from 'mobx'

import { Page, PageHeader } from '../../elements'
import {
  DataAlertSubHeader,
  DataAlertText,
  ContentSubHeader,
  DataAlertLI,
} from '../../elements/styled'
import { Spacer } from '../../../../components'
import { numberToDollars } from '../../../../utils'
import {
  YEAR_IN_DAYS,
  daysSinceUserInfo,
  isOutdatedInvestment,
  ownerFirstName,
} from '../../functions'

// Lists are rebuilt from scratch on each pass; compare by id so an equivalent
// result reuses the existing array instead of triggering another render.
const sameAccounts = (a, b) =>
  a.length === b.length && a.every((account, index) => account.id === b[index].id)

function UpdateInfoAlert(props) {
  const {
    store: { accounts, person, spouse, allPensions, goals },
    getInvestments,
    page,
  } = props

  const [outdatedAccounts, setOutdatedAccounts] = useState([])
  const [pensionPayouts, setPensionPayouts] = useState([])
  const [isLessThanYear, setIsLessThanYear] = useState(false)
  const [isMoreThanYear, setIsMoreThanYear] = useState(false)

  const showOutdated = outdatedAccounts.length > 0
  const showLumpSum = pensionPayouts.length > 0

  const isMounted = useRef(true)

  useEffect(() => {
    isMounted.current = true

    return () => {
      isMounted.current = false
    }
  }, [])

  const shortListAccounts = accounts.filter(
    account => account.type === 'Institutional' || account.type === 'NonGc'
  )
  // The filter above allocates a new array on every render, which would give
  // getAllInvestments a new identity every render and re-run the fetch effect
  // endlessly. Key the memo off the account ids so it only changes when the
  // set of accounts actually does.
  const shortListKey = shortListAccounts.map(account => account.id).join(',')
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const accountsToScan = useMemo(() => shortListAccounts, [shortListKey])

  // Only goals with money due out this year that has not been withdrawn yet
  // still count as a data alert; future-dated goals do not.
  const goalWithdrawalsNeeded = (goals ?? []).filter(goal => goal.needsWithdrawalUpdate)
  const showGoalWithdrawal = goalWithdrawalsNeeded.length > 0

  const getAllInvestments = useCallback(async () => {
    const staleAccounts = []
    let anyLessThanYear = false
    let anyMoreThanYear = false

    for (const account of accountsToScan) {
      const id = account.id
      let type = account.type

      switch (type) {
        case 'Institutional':
          type = 'institutional'
          break
        case 'NonGc':
          type = 'non-gc'
          break
        default:
          break
      }
      const accountInv = await getInvestments({ type, id })

      // One outdated investment is enough to flag the whole account.
      const outdatedInvestment = accountInv.find(isOutdatedInvestment)

      if (outdatedInvestment) {
        if (daysSinceUserInfo(outdatedInvestment) > YEAR_IN_DAYS) {
          anyMoreThanYear = true
        } else {
          anyLessThanYear = true
        }
        staleAccounts.push(toJS(account))
      }
    }

    if (!isMounted.current) return

    setIsLessThanYear(anyLessThanYear)
    setIsMoreThanYear(anyMoreThanYear)
    setOutdatedAccounts(current => (sameAccounts(current, staleAccounts) ? current : staleAccounts))
  }, [getInvestments, accountsToScan])

  const showLump = useCallback(() => {
    const pensions = allPensions
      .filter(account => account.paymentType === 'L')
      .filter(account => account.lumpsumreceived === 'N')

    let payouts

    if (person.includeSpouse) {
      const spousePensions = pensions
        .filter(account => account.ownerId === spouse.id)
        .filter(account => account.pensionStartAge <= spouse.age)
      const primaryPensions = pensions
        .filter(account => account.ownerId === person.id)
        .filter(account => account.pensionStartAge <= person.age)

      payouts = [...primaryPensions, ...spousePensions]
    } else {
      // <= rather than an exact age match, so a payout age that passed in an
      // earlier year is still flagged (GC-21652), matching the couple path.
      payouts = pensions.filter(account => account.pensionStartAge <= person.age)
    }

    setPensionPayouts(current => (sameAccounts(current, payouts) ? current : payouts))
  }, [allPensions, person.age, person.id, person.includeSpouse, spouse?.age, spouse?.id])

  useEffect(() => {
    try {
      getAllInvestments()
      showLump()
    } catch (err) {
      console.error(err)
    }
  }, [getAllInvestments, showLump])

  const outdatedAccts = () => {
    return (
      <>
        {outdatedAccounts.map(acct => {
          const owner = ownerFirstName(acct.participantId, person, spouse)

          return (
            <DataAlertLI key={acct.id}>
              {acct.name}
              {owner && ` (${owner}'s)`}
            </DataAlertLI>
          )
        })}
      </>
    )
  }

  const goalWithdrawals = () => {
    return goalWithdrawalsNeeded.map(goal => {
      const startYear = goal.disbursements[0].startYear
      const endYear = goal.disbursements[goal.disbursements.length - 1].startYear
      let year = startYear

      if (startYear !== endYear) {
        year = startYear + '-' + endYear
      }

      return (
        <DataAlertLI key={goal.id}>
          {goal.name}—{year} amount needed: ${numberToDollars(goal.totalRemaining, true)}
        </DataAlertLI>
      )
    })
  }
  const lumpSum = () => {
    return pensionPayouts.map(pension => {
      const owner = ownerFirstName(pension.ownerId, person, spouse)

      return (
        <DataAlertLI key={pension.id}>
          {pension.pensionName}
          {owner && ` (${owner}'s)`}—amount expected: $
          {numberToDollars(pension.monthlyPensionIncome, true)}
        </DataAlertLI>
      )
    })
  }

  if (showGoalWithdrawal || showLumpSum || showOutdated) {
    return (
      <Page header={<PageHeader text='Snapshot' />} page={page}>
        <ContentSubHeader>DATA ALERT STILL IN EFFECT</ContentSubHeader>
        <DataAlertText>
          Be sure to update your data as soon as you can. The advice and projections we've provided
          have been impacted by the following issue(s), possible to a significant degree. Entering
          updated information may require other changes to your strategy to reach your financial
          goals.
        </DataAlertText>

        {showGoalWithdrawal && (
          <>
            <Spacer space='20px' />
            <DataAlertSubHeader>Goal withdrawal</DataAlertSubHeader>
            <Spacer space='5px' />
            {goalWithdrawals()}
            <Spacer space='5px' />
            <DataAlertText>
              We consider this amount as money yet to be withdrawn from investment accounts.
            </DataAlertText>
          </>
        )}

        {showLumpSum && (
          <>
            <Spacer space='20px' />
            <DataAlertSubHeader>Lump sum pension payout</DataAlertSubHeader>
            <Spacer space='5px' />
            {lumpSum()}
            <Spacer space='5px' />
            <DataAlertText>
              We consider this amount expected income that will be rolled into a tax-advantaged
              account.
            </DataAlertText>
          </>
        )}

        {showOutdated && (
          <>
            <Spacer space='20px' />
            <DataAlertSubHeader>Outdated investment balances</DataAlertSubHeader>
            <Spacer space='5px' />
            {outdatedAccts()}
            <Spacer space='5px' />
            <DataAlertText>
              Some of your investment balance information is {isLessThanYear && `6-12 months old.`}
              {isMoreThanYear && `more than a year old.`} The greater the difference between the
              balance in our database and the actual balance, the greater the impact on your advice
              and income projections.
            </DataAlertText>
          </>
        )}
      </Page>
    )
  } else {
    return null
  }
}

export default inject('store')(observer(UpdateInfoAlert))
